import { getFirst, isValid } from 'clients/Clients';
import SupplierClient from 'clients/SupplierClient';
import { getBrandInfo } from 'clients/WebServiceClient';
import DetailProductPcScreenNew from 'components-v2/organisms/pages/detail-product-new/PcScreen';
import ProductDetailNotLogin from 'components-v2/organisms/pages/detail-product-new/PcScreenNotLogin';
import Template from 'components/layout/Template';
import ProductDetailLoading from 'components/organisms/ProductDetailLoading';
import { HCM_PROVINCE_CODE } from 'constants/data';
import { HTTP_STATUS } from 'constants/Enums/http';
import { useAuth } from 'context/Auth';
import { useProduct } from 'context/Product/context';
import useDealTagUrl from 'hooks/useDealTagUrl';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import ProductServiceV2, { getDetailProductWithoutLogin, getLiteProduct } from 'services/ProductServiceV2';
import { doWithServerSide } from 'services/SsrService';
import WebService from 'services/WebService';
import gtag from 'utils/gtag';
import { getLinkCDNImage, getLinkCDNImageList } from 'utils/ImageUtils';
import { titleCase } from 'utils/StringUtils';
import { sellerForStore } from 'utils/StoreUtils';
import { useStore } from 'zustand-lib/storeGlobal';
import useSearchResult from 'zustand-lib/useSearchResult';

/**
 * Update UI mobile v2
 * August 23, 2022
 * https://buymed.atlassian.net/browse/APO-715
 */

export async function getServerSideProps(ctx) {
  return doWithServerSide(
    ctx,
    async (ctx, customer) => {
      // Logged-in: read the same authed detail endpoint the client uses, so
      // SSR 404s exactly when the customer themselves can't buy it (province,
      // certificates, seller visibility, etc.) — no need for the guest-only
      // lite endpoint. Guest: the lite endpoint is enough to decide 404, and
      // is region-agnostic (buyable in one region only still 200s).
      const productRes = customer
        ? await WebService.getDataProductDetail({ ctx, productDetailPage: true })
        : await getLiteProduct({ ctx });

      if (!isValid(productRes)) {
        // Only a genuine NOT_FOUND should 404 the page; a transient upstream
        // error (timeout, blip) should fail open and let the client retry.
        if (productRes?.status === HTTP_STATUS.NotFound) {
          return { notFound: true };
        }
        return { props: { SEO_CONFIG: {} } };
      }

      const product = getFirst(productRes);
      const nameTitle = titleCase(product?.name || '');

      return {
        props: {
          SEO_CONFIG: {
            title: nameTitle,
            description: nameTitle,
            ogTitle: nameTitle,
            ogDescription: nameTitle,
            ogImage: getLinkCDNImage(product?.imageUrl) || getLinkCDNImageList(product?.imageUrls)[0] || '',
            ogImageAlt: nameTitle,
          },
        },
      };
    },
    {
      initZustand: ['tabs', 'tags'],
    },
  );
}

export default function ProductDetail() {
  const router = useRouter();
  const [product, setProduct] = useState({});
  const [loading, setLoading] = useState(false);
  const [recommendProductsNoLogin, setRecommendProductsNoLogin] = useState([]);

  const { user } = useAuth();
  const getSellerByCode = useStore((state) => state.getSellerByCode);
  const getSearchResult = useSearchResult((state) => state.getSearchResult);
  const sendSearchEvent = useSearchResult((state) => state.sendSearchEvent);

  useEffect(() => {
    async function fetchProduct() {
      const slug = router.query.slug || '';
      const codeLocationNotLogin = Cookies.get('provinceCode') || HCM_PROVINCE_CODE;

      try {
        if (!user) {
          const [productWithoutLoginRes, productWithoutLoginRecommend] = await Promise.all([
            getDetailProductWithoutLogin({ slug }),
            ProductServiceV2.getListProductWithoutLogin({ slug, provinceCode: codeLocationNotLogin }),
          ]);
          if (!isValid(productWithoutLoginRes)) {
            return;
          }

          const productRawNotLogin = ProductServiceV2.convertProductV2(getFirst(productWithoutLoginRes));

          const sellerRes = await SupplierClient.getSellersV2([productRawNotLogin.seller.code]);
          const seller = getFirst(sellerRes);
          // thông tin nhà bán theo gian chứa SKU (BV-2164)
          productRawNotLogin.sellerInfo = sellerForStore({ ...seller, ...productRawNotLogin.sellerInfo }, productRawNotLogin.storeCode);

          setProduct(productRawNotLogin);
          setRecommendProductsNoLogin(productWithoutLoginRecommend?.data?.length > 0 ? productWithoutLoginRecommend?.data : []);
        } else {
          setLoading(true);
          const productRes = await WebService.getDataProductDetail({
            router,
            slug: router.query.slug,
            isConvertProduct: false,
            productDetailPage: true,
            isGetCrossSell: true, // BV-2271: SP hay mua kèm
          });
          if (isValid(productRes)) {
            const product = getFirst(productRes);

            const sellerRes = await SupplierClient.getSellersV2([product.sellerInfo.sellerCode]);
            const seller = getFirst(sellerRes);

            const productConverted = ProductServiceV2.convertProductV2(product);
            // thông tin nhà bán theo gian chứa SKU (BV-2164)
            productConverted.sellerInfo = sellerForStore(
              {
                ...seller,
                ...productConverted.sellerInfo,
              },
              productConverted.storeCode,
            );

            // TODO: call manual fetch when cache not working
            if (productConverted?.productId && !productConverted?.isCombo) {
              const brandInfo = await getBrandInfo({ productID: productConverted.productId });
              if (isValid(brandInfo)) productConverted.brandInfo = getFirst(brandInfo).brandInfo;
            }

            setProduct(productConverted);
          } else {
            return;
          }
        }
      } catch (error) {
        setLoading(false);
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
    fetchProduct();
  }, [router.query.slug]);

  useEffect(() => {
    if (!product.productId) return;

    // Gửi event SEARCH nếu click xem chi tiết một sản phẩm trong kết quả search
    const searchResult = getSearchResult()?.[router?.query?.text || router?.query?.q];
    const isProductInSearchResult = searchResult?.data?.[product.productId];

    if (isProductInSearchResult) {
      sendSearchEvent(searchResult?.content);
    }
  }, [router, product.productId]);

  useEffect(() => {
    if (!product?.sellerCode) return;

    (async () => {
      const sellerInfo = await getSellerByCode(product?.sellerCode);
      gtag.viewContentDetail({ product: { ...product, sellerInfo } });
      gtag.viewContent({ product: { ...product, sellerInfo } });
    })();
  }, [product?.sellerCode]);

  const { addProductToListViewed } = useProduct();
  useEffect(() => {
    if (!product?.sku) return;

    addProductToListViewed(product?.sku || '');
  }, [product?.sku]);

  return (
    <Template
      overrideMV2Options={{ title: 'Chi tiết sản phẩm' }}
      pageTitle={product.name}
      title={product.name}
      pageName="PRD_DETAIL"
      product={product}
      isNewLayout={user?.isNewLayout}
    >
      {user ? (
        <RenderNewUIDetail user={user} product={product} loading={loading} />
      ) : (
        <RenderNotLogin product={product} productWithoutLoginRecommend={recommendProductsNoLogin} />
      )}
    </Template>
  );
}

function RenderNewUIDetail({ loading, product, user }) {
  const { categoryCodes, tags, seller, deal = {} } = product || {};

  const link = useDealTagUrl(product);

  if (loading) return <ProductDetailLoading isLogin />;
  if (Object.keys(product).length <= 0) return null;

  return (
    <DetailProductPcScreenNew
      product={product}
      user={user}
      deal={deal}
      orderedCount={product.orderedCount}
      sellerInfo={product.sellerInfo}
      seller={seller}
      categoryCodes={categoryCodes}
      link={link}
      tags={tags}
    />
  );
}

function RenderNotLogin({ product, productWithoutLoginRecommend }) {
  if (!Object.keys(product).length) return <ProductDetailLoading isLogin={false} />;

  return <ProductDetailNotLogin product={product} orderedCount={product.orderedCount} productWithoutLoginRecommend={productWithoutLoginRecommend} />;
}
